/**
 * JeanieIQ Assist — the tenant's side.
 *
 * Shane, 2026-08-31: *"when we are on there tennent platform there needs to be a
 * bar on the to stating we are on... that is what we see now bit so should
 * they"*. Until now the impersonation banner was visible only to the admin doing
 * the impersonating, which is exactly the wrong way round.
 */
import { apiFetch } from './api';

export interface AssistGrant {
  id: string;
  /** pending | active | declined | ended | expired */
  status: string;
  reason: string;
  ticketRef: string | null;
  requestedByEmail: string;
  requestedAt: string;
  respondedAt: string | null;
  expiresAt: string | null;
  endedAt: string | null;
  endedReason: string | null;
  /** Last moment JeanieIQ was actually served under this grant. */
  lastSeenAt: string | null;
  /** read_only | full — what the tenant allowed us to touch. */
  accessLevel: string;
}

/** The live request or session. Null when nobody is asking and nobody is in. */
export async function fetchCurrentAssist(signal?: AbortSignal): Promise<AssistGrant | null> {
  return apiFetch('/api/assist/current', signal ? { signal } : {});
}

/** Every request, who answered it, and how it ended. */
export async function fetchAssistHistory(signal?: AbortSignal): Promise<AssistGrant[]> {
  return apiFetch('/api/assist', signal ? { signal } : {});
}

export async function respondToAssist(
  grantId: string,
  accept: boolean,
  hours?: number,
  /** Omitted means look-only — the least access, never the most. */
  accessLevel?: 'read_only' | 'full',
): Promise<AssistGrant> {
  return apiFetch(`/api/assist/${grantId}/respond`, {
    method: 'POST',
    body: {
      accept,
      ...(hours === undefined ? {} : { hours }),
      ...(accessLevel === undefined ? {} : { accessLevel }),
    },
  });
}

/** Ends it now. The next request JeanieIQ makes is refused. */
export async function disconnectAssist(): Promise<{ ended: number }> {
  return apiFetch('/api/assist/disconnect', { method: 'POST' });
}

/**
 * THE OTHER DIRECTION — the tenant asking us (2026-09-24).
 *
 * Shane: *"its 2 step they ask we see and accept they agree entry"*. Raising one
 * grants nothing and asks for nothing: it puts the tenant in our queue. If we
 * then need to be inside their platform to fix it, that arrives separately as an
 * ordinary access request they still have to approve.
 */
export interface AssistRequest {
  id: string;
  /** ASK-YYYY-NNN. The reference a support call is about. */
  reference: string;
  subject: string;
  body: string;
  /** low | normal | high */
  urgency: string;
  /** open | accepted */
  status: string;
  raisedByEmail: string;
  raisedAt: string;
  acceptedAt: string | null;
  /** The access request our acceptance raised, when one was needed. */
  grantId: string | null;
  /** What it turned out to be. Null until somebody closes it. */
  resolution: string | null;
  resolvedAt: string | null;
  /** tenant | jeanieiq — which side closed it. */
  resolvedBy: 'tenant' | 'jeanieiq' | null;
  /**
   * Set when JeanieIQ sent it to their engineers. The ONE thing a tenant learns
   * about an escalation — so the thread says something instead of going quiet.
   * The diagnosis, who is on it and every internal note stay on JeanieIQ's side.
   */
  escalatedAt: string | null;
}

export async function fetchAssistRequests(signal?: AbortSignal): Promise<AssistRequest[]> {
  return apiFetch('/api/assist/requests', signal ? { signal } : {});
}

export async function raiseAssistRequest(input: {
  subject: string;
  body: string;
  urgency?: 'low' | 'normal' | 'high';
}): Promise<AssistRequest> {
  return apiFetch('/api/assist/requests', { method: 'POST', body: input });
}

/**
 * The conversation on a request. Shane, 2026-09-24: *"there needs to be a way to
 * communicate ie the chat"*.
 *
 * `from` is derived server-side from which sender column is filled, so it cannot
 * disagree with the row. Never render a side from anything else.
 */
export interface AssistMessage {
  id: string;
  body: string;
  sentAt: string;
  /** tenant | jeanieiq */
  from: 'tenant' | 'jeanieiq';
  /** Their email as it was when they wrote it. */
  sender: string;
}

export async function fetchAssistMessages(
  requestId: string,
  signal?: AbortSignal,
): Promise<AssistMessage[]> {
  return apiFetch(`/api/assist/requests/${requestId}/messages`, signal ? { signal } : {});
}

export async function postAssistMessage(
  requestId: string,
  body: string,
): Promise<AssistMessage> {
  return apiFetch(`/api/assist/requests/${requestId}/messages`, {
    method: 'POST',
    body: { body },
  });
}

/**
 * Close one. Shane, 2026-09-24: *"both sides need to be able to end"*. The tenant
 * closes what has stopped mattering; JeanieIQ closes what it has finished. The
 * note is required either way — it is the part still worth reading in a month.
 *
 * Closing shuts no door: an access grant is ended by Disconnect or by its expiry.
 */
export async function resolveAssistRequest(
  requestId: string,
  resolution: string,
): Promise<AssistRequest> {
  return apiFetch(`/api/assist/requests/${requestId}/resolve`, {
    method: 'POST',
    body: { resolution },
  });
}
